import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import StudentPortal from "@/pages/StudentPortal";
import AdminDashboard from "@/pages/AdminDashboard";
import MigrationPage from "@/pages/MigrationPage";
import DevToolsBlocker from "@/components/DevToolsBlocker";
import ErrorBoundary from "@/components/ErrorBoundary";
import ExternalMockBooking from "@/pages/ExternalMockBooking";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route wrapper (redirect to appropriate dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect based on user role
    if (user?.role === 'admin' || user?.role === 'staff' || user?.role === 'moderator') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/portal" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  
  // Determine default route based on role
  const getDefaultRoute = () => {
    if (!user) return '/login';
    if (user.role === 'admin' || user.role === 'staff' || user.role === 'moderator') return '/admin';
    return '/portal';
  };
  
  return (
    <Routes>
      <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        } 
      />
      {/* <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        } 
      /> */}
      {/* External Mock Booking - Public page, no auth needed */}
      <Route path="/mock-booking" element={<ExternalMockBooking />} />
      <Route 
        path="/portal" 
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <StudentPortal />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <AdminDashboard />
            </ErrorBoundary>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/migration" 
        element={
          <ProtectedRoute>
            <MigrationPage />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DevToolsBlocker />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
